import {
  Action,
  Actions,
  NgxsOnInit,
  ofActionDispatched,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { AuthService } from '@app/modules/auth/service/auth.service';
import { Injectable } from '@angular/core';
import { AuthActions } from '@app/modules/auth/store/auth.actions';
import { Observable } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { UserModel } from '@app/modules/user/model/user';
import { isTokenExpired } from '@app/modules/auth/utils/token-expiry';
import { UserActions } from '@app/modules/user/store/user.actions';
import { UserStorageService } from '@app/modules/user/service/user-storage.service';

interface AuthStateModel {
  loading: boolean;
  isAuthorized: boolean;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    loading: false,
    isAuthorized: false,
  },
})
@Injectable()
export class AuthState implements NgxsOnInit {
  @Selector()
  static loading(state: AuthStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static isAuthorized(state: AuthStateModel): boolean {
    return state.isAuthorized;
  }

  /**
   * @param store
   * @param actions
   * @param authService
   */
  constructor(
    private readonly store: Store,
    private readonly actions: Actions,
    private readonly authService: AuthService,
    private readonly storage: UserStorageService
  ) {}

  ngxsOnInit(ctx: StateContext<AuthStateModel>): void {
    ctx.dispatch([new AuthActions.IsInviteURL()]);
  }

  @Action(AuthActions.IsInviteURL)
  isInviteURL(ctx: StateContext<AuthStateModel>): void {
    const queries = window.location.search.slice(1).split('&');
    if (queries.length) {
      for (const query of queries) {
        const [key, value] = query.split('=');

        if (key === 'invitedAs') {
          this.store.dispatch(new UserActions.RemoveUserFromStorage());
          return;
        }
      }
    }
    this.store.dispatch(new UserActions.GetUserFromStorage());
  }

  @Action(
    [
      UserActions.GetUserFromStorage,
      UserActions.UpdateUserInStorage,
      UserActions.RemoveUserFromStorage,
    ],
    { cancelUncompleted: true }
  )
  onUserChange(ctx: StateContext<AuthStateModel>): void {
    const user = this.store.selectSnapshot<UserModel>(
      (state) => state.user.model
    );
    // A token that exists but has expired is NOT a session. Treating it as
    // one made the app boot "authorized", navigate to /office, 401 on every
    // call, sign itself out, and bounce back — a redirect loop that presents
    // as a spinner that never stops.
    const isAuthorized = !!user.token && !!user.role && !isTokenExpired(user.token);
    ctx.patchState({ isAuthorized });
  }

  @Action(AuthActions.Registration, { cancelUncompleted: true })
  registration(
    ctx: StateContext<AuthStateModel>,
    { value }: AuthActions.Registration
  ): Observable<void | string> {
    ctx.patchState({ loading: true });

    return this.authService.registration(value).pipe(
      tap((cognitoId: string) => {
        const user = { email: value.email, password: value.password, pin: value.pin };
        this.store.dispatch(new UserActions.UpdateUserInStorage(user));
        this.actions
          .pipe(ofActionDispatched(UserActions.CreateUserSuccess), take(1))
          .subscribe(() => ctx.dispatch(new AuthActions.RegistrationSuccess()));

        this.actions
          .pipe(ofActionDispatched(UserActions.CreateUserFailed), take(1))
          .subscribe(({ error }) =>
            ctx.dispatch(new AuthActions.RegistrationFailed(error))
          );

        this.store.dispatch(new UserActions.CreateUserSuccess());
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new AuthActions.RegistrationFailed(e))
      )
    );
  }

  @Action(AuthActions.RegistrationConfirm, { cancelUncompleted: true })
  registrationConfirm(
    ctx: StateContext<AuthStateModel>,
    { value }: AuthActions.RegistrationConfirm
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });

    return this.authService.registrationConfirm(value).pipe(
      tap(() => {
        this.store.dispatch(new UserActions.RemoveUserFromStorage());
        ctx.dispatch(new AuthActions.RegistrationConfirmSuccess());
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new AuthActions.RegistrationConfirmFailed(e))
      )
    );
  }

  @Action(AuthActions.Login)
  login(
    ctx: StateContext<AuthStateModel>,
    { value }: AuthActions.Login
  ): Observable<void | string> {
    ctx.patchState({ loading: true });
    return this.authService.login(value).pipe(
      tap((token: string) => {
        ctx.dispatch(new UserActions.UpdateUserInStorage({ token }));

        this.actions
          .pipe(ofActionDispatched(UserActions.GetUserSuccess), take(1))
          .subscribe(() => ctx.dispatch(new AuthActions.LoginSuccess()));

        this.actions
          .pipe(ofActionDispatched(UserActions.GetUserFailed), take(1))
          .subscribe(({ error }) => {
            ctx.dispatch(new UserActions.RemoveUserFromStorage());
            ctx.dispatch(new AuthActions.LoginFailed(error));
          });

        this.store.dispatch(new UserActions.GetUser());
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new AuthActions.LoginFailed(e))
      )
    );
  }

  @Action(AuthActions.GoogleLogin)
  googleLogin(
    ctx: StateContext<AuthStateModel>,
    { idToken }: AuthActions.GoogleLogin
  ): Observable<void | string> {
    ctx.patchState({ loading: true });
    return this.authService.googleLogin(idToken).pipe(
      tap((token: string) => {
        ctx.dispatch(new UserActions.UpdateUserInStorage({ token }));

        this.actions
          .pipe(ofActionDispatched(UserActions.GetUserSuccess), take(1))
          .subscribe(() => ctx.dispatch(new AuthActions.LoginSuccess()));

        this.actions
          .pipe(ofActionDispatched(UserActions.GetUserFailed), take(1))
          .subscribe(({ error }) => {
            ctx.dispatch(new UserActions.RemoveUserFromStorage());
            ctx.dispatch(new AuthActions.LoginFailed(error));
          });

        this.store.dispatch(new UserActions.GetUser());
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new AuthActions.LoginFailed(e))
      )
    );
  }

  @Action(AuthActions.Logout, { cancelUncompleted: true })
  logout(ctx: StateContext<string>): Observable<void | string> {
    // The local session is cleared on BOTH paths. "Sign out" on a shared or
    // borrowed device must leave nothing behind even when the server call
    // fails — previously a failed request left the user signed in locally,
    // which is the one moment a person is relying on it having worked.
    return this.authService.logout().pipe(
      tap(() => {
        ctx.dispatch(new UserActions.RemoveUserFromStorage());
        ctx.dispatch(new AuthActions.LogoutSuccess());
      }),
      catchError((e: HttpErrorResponse) => {
        ctx.dispatch(new UserActions.RemoveUserFromStorage());
        return ctx.dispatch(new AuthActions.LogoutFailed(e));
      })
    );
  }

  @Action(AuthActions.PasswordChange, { cancelUncompleted: true })
  passwordChange(
    ctx: StateContext<AuthStateModel>,
    { value }: AuthActions.PasswordChange
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });

    return this.authService.passwordChange(value).pipe(
      tap(() => ctx.dispatch(new AuthActions.PasswordChangeSuccess())),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new AuthActions.PasswordChangeFailed(e))
      )
    );
  }

  @Action(AuthActions.PasswordReset, { cancelUncompleted: true })
  passwordReset(
    ctx: StateContext<AuthStateModel>,
    { value }: AuthActions.PasswordReset
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });

    return this.authService.passwordReset(value).pipe(
      tap(() => ctx.dispatch(new AuthActions.PasswordResetSuccess(value))),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new AuthActions.PasswordResetFailure(e))
      )
    );
  }

  @Action([
    AuthActions.RegistrationSuccess,
    AuthActions.RegistrationFailed,
    AuthActions.RegistrationConfirmSuccess,
    AuthActions.RegistrationConfirmFailed,
    AuthActions.LoginSuccess,
    AuthActions.LoginFailed,
    AuthActions.LogoutSuccess,
    AuthActions.LogoutFailed,
    AuthActions.PasswordRestoreSuccess,
    AuthActions.PasswordRestoreFailed,
    AuthActions.PasswordRestoreConfirmSuccess,
    AuthActions.PasswordRestoreConfirmFailed,
    AuthActions.PasswordChangeSuccess,
    AuthActions.PasswordChangeFailed,
    AuthActions.PasswordResetSuccess,
    AuthActions.PasswordResetFailure

  ])
  stopLoading(ctx: StateContext<AuthStateModel>): void {
    ctx.patchState({ loading: false });
  }
}
